<template>
  <b-container fluid class="menu-editor-root">
    <b-row class="menu-editor-toolbar">
      <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav" style="width: 100%;">
        <b-collapse is-nav id="nav_dropdown_collapse">
          <b-navbar-nav>
            <b-nav-item>
              <b-button @click.prevent="goBackToDesign">
                <font-awesome-icon icon="arrow-left" /> back to design
              </b-button>
            </b-nav-item>
            <b-nav-item>
              <b-button @click.prevent="triggerLogoUpload">
                <font-awesome-icon icon="image" /> upload logo
              </b-button>
            </b-nav-item>
            <b-nav-item v-if="logoBase64">
              <b-button class="remove-logo-btn" @click.prevent="removeLogo">
                <font-awesome-icon icon="times" /> remove logo
              </b-button>
            </b-nav-item>
            <b-nav-item>
              <span class="app-name-toolbar">
                <font-awesome-icon icon="heading" class="app-name-icon" />
                <input
                  v-model="appDisplayName"
                  class="app-name-input"
                  placeholder="App name..."
                />
              </span>
            </b-nav-item>
          </b-navbar-nav>
          <b-navbar-nav class="ms-auto">
            <b-nav-item>
              <b-button
                :disabled="!hasChanges || saving"
                @click="saveMenu"
                :class="{ 'save-active': hasChanges }"
              >
                <template v-if="saving">saving...</template>
                <template v-else-if="saved">saved!</template>
                <template v-else>save menu</template>
              </b-button>
            </b-nav-item>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    </b-row>

    <b-row class="menu-editor-panels">
      <!-- Left panel: Available Pages -->
      <b-col cols="4" class="available-pages-col">
        <div class="panel-header">
          <div class="panel-title">Available Pages</div>
          <div class="panel-desc">Drag pages to the menu, or click + to add.</div>
        </div>
        <div class="available-pages-list">
          <draggable
            :list="availablePages"
            :group="{ name: 'menu', pull: 'clone', put: false }"
            :clone="onCloneFromAvailable"
            :sort="false"
            :move="checkMenuMove"
            item-key="uniqueTitle"
          >
            <template #item="{ element: page }">
              <div
                class="available-page-item"
                role="listitem"
                :aria-label="page.title"
                :class="{ 'already-in-menu': page.alreadyInMenu }"
              >
                <font-awesome-icon icon="grip-vertical" class="page-drag-icon" />
                <font-awesome-icon icon="file" class="page-icon" />
                <span class="page-label">{{ page.title }}</span>
                <span
                  v-if="page.alreadyInMenu"
                  class="in-menu-badge"
                >in menu</span>
                <span
                  v-else
                  class="add-page-btn"
                  @click="addPageToMenu(page)"
                >+</span>
              </div>
            </template>
          </draggable>
        </div>
      </b-col>

      <!-- Right panel: Menu Layout -->
      <b-col cols="8" class="menu-layout-col">
        <div class="panel-header">
          <div class="panel-title">Menu Bar Layout</div>
          <div class="panel-desc">
            Drag to reorder. Click labels to rename. Add a value to show a formula over the matching records. Drop a page onto a value to make it a link. Menus can nest inside menus, up to 4 levels. The align button on a top-level item pins it to the right side of the menu. The shield on a link shows or hides that link for a signed-in user.
            <span v-if="hasChanges" class="unsaved-badge">unsaved changes</span>
          </div>
        </div>

        <div class="menu-items-container">
          <draggable
            v-model="menuItems"
            :group="{ name: 'menu', pull: true, put: true }"
            item-key="id"
            handle=".menu-drag-handle"
            filter=".dropdown-children"
            :preventOnFilter="false"
            ghost-class="drag-ghost"
            :empty-insert-threshold="80"
            :move="checkMenuMove"
            data-menu-depth="1"
            class="menu-items-draggable"
          >
            <template #item="{ element: item, index }">
              <MenuItemNode
                :item="item"
                :depth="1"
                @remove="removeMenuItem(index)"
              />
            </template>
            <template #footer>
              <div v-if="menuItems.length === 0" class="empty-menu-hint">
                No menu items yet. Drag pages from the left panel, click + to add them, or add a value.
              </div>
            </template>
          </draggable>
        </div>

        <div class="menu-actions">
          <button class="add-dropdown-btn" @click="addDropdownGroup">
            <font-awesome-icon icon="plus" /> add dropdown group
          </button>
          <button class="add-dropdown-btn" @click="addValueItem">
            <font-awesome-icon icon="hashtag" /> add value
          </button>
        </div>

        <!-- Preview section -->
        <div class="preview-section">
          <div class="panel-title">Preview</div>
          <nav class="preview-navbar">
            <img v-if="logoBase64" :src="logoBase64" class="preview-logo" />
            <span v-if="appDisplayName" class="preview-app-name">{{ appDisplayName }}</span>
            <span v-if="menuItems.length === 0 && !logoBase64 && !appDisplayName" class="preview-empty">Menu is empty</span>
            <template v-for="(group, groupIndex) in previewGroups" :key="groupIndex">
              <span v-if="groupIndex === 1" class="preview-right-gap"></span>
              <template v-for="item in group" :key="item.id">
                <span v-if="item.type === 'link'" class="preview-link">
                  {{ item.label }}
                </span>
                <span v-else-if="item.type === 'value'" class="preview-value" :class="{ 'preview-value-link': item.pageUniqueTitle && !(item.children && item.children.length) }" :title="item.pageUniqueTitle || ''">
                  <font-awesome-icon v-if="item.requiresLogin" icon="lock" class="preview-lock" title="Shown only when logged in" />
                  <span v-if="item.label">{{ item.label }}</span>
                  <span class="preview-value-badge">{{ previewText(item) }}</span>
                  <font-awesome-icon v-if="item.children && item.children.length" icon="caret-down" style="font-size: 0.7em;" />
                </span>
                <span v-else class="preview-dropdown">
                  {{ item.label }} <font-awesome-icon icon="caret-down" style="font-size: 0.7em;" />
                </span>
              </template>
            </template>
          </nav>
        </div>
      </b-col>
    </b-row>

    <input
      type="file"
      ref="logoInput"
      accept="image/*"
      style="display: none;"
      @change="onLogoSelected"
    />

    <UserPermissionRuleEditor
      :visible="permissionEditorVisible"
      :rules="permissionEditorRules"
      :spreadsheets="spreadsheets"
      :pages="pages"
      subject="this link"
      :application-unique-title="application"
      @update:visible="permissionEditorVisible = $event"
      @save="saveLinkPermissionRules"
    />
  </b-container>
</template>

<script>

  import draggable from 'vuedraggable'
  import _ from 'lodash'
  import MenuItemNode from './MenuItemNode.vue'
  import normalizeMenuItems from './normalize-menu-items.js'
  import canPlaceMenuItem from './menu-nesting.js'
  import menuItemsOnSide from '../../../../superturbodb/src/menu-align.js'
  import UserPermissionRuleEditor from '../lib/UserPermissionRuleEditor.vue'
  import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'

  export default {
    name: 'MenuEditor',
    components: { draggable, MenuItemNode, UserPermissionRuleEditor },
    provide () {

      return { menuEditor: this }
    },
    data () {

      return {
        pages: [],
        spreadsheets: [],
        menuItems: [],
        savedMenuItems: [],
        saving: false,
        saved: false,
        editingItemId: null,
        logoBase64: null,
        savedLogoBase64: null,
        appDisplayName: '',
        savedAppDisplayName: '',
        previewValues: {},
        previewRequestId: 0,
        permissionEditorVisible: false,
        permissionTarget: null
      }
    },
    created () {

      this.queuePreviewValues = _.debounce(() => {

        this.loadPreviewValues()
      }, 300)
    },
    watch: {
      menuItems: {
        deep: true,
        handler () {

          if (this.queuePreviewValues) this.queuePreviewValues()
        }
      }
    },
    computed: {
      application () {

        return this.$route.params.application
      },
      hasChanges () {

        return !_.isEqual(this.menuItems, this.savedMenuItems) || this.logoBase64 !== this.savedLogoBase64 || this.appDisplayName !== this.savedAppDisplayName
      },
      appHasLogin () {

        const hasLogin = (components) => {

          if (!components) return false

          for (const comp of components) {

            if (comp.type === 'login' || comp.type === 'registration') return true
            if (comp.children && hasLogin(comp.children)) return true
            if (comp.components && hasLogin(comp.components)) return true
          }

          return false
        }

        return this.pages.some(page => hasLogin(page.components))
      },
      previewGroups () {

        const right = menuItemsOnSide(this.menuItems, 'right')
        const left = menuItemsOnSide(this.menuItems, 'left')

        return right.length ? [left, right] : [left]
      },
      permissionEditorRules () {

        return (this.permissionTarget && this.permissionTarget.userPermissionRules) || { rules: [] }
      },
      availablePages () {

        const usedPageTitles = new Set()

        const collectLinks = (items) => {

          for (const item of items || []) {

            if (item.type === 'link' && item.pageUniqueTitle) usedPageTitles.add(item.pageUniqueTitle)

            collectLinks(item.children)
          }
        }

        collectLinks(this.menuItems)

        return this.pages
          .filter(p => !p.parentUniqueTitle)
          .map(p => ({
            ...p,
            alreadyInMenu: usedPageTitles.has(p.uniqueTitle)
          }))
      }
    },
    async mounted () {

      await Promise.all([this.loadPages(), this.loadSettings(), this.loadSpreadsheets()])
      this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
    },
    beforeUnmount () {

      if (this._stopDesignerSync) this._stopDesignerSync()
    },
    beforeRouteLeave (to, from, next) {

      if (this.hasChanges) {

        const answer = window.confirm('You have unsaved menu changes. Discard them?')

        if (!answer) {

          return next(false)
        }
      }

      next()
    },
    methods: {
      async loadPages () {

        try {

          const res = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {
            headers: { 'application-unique-title': this.application }
          })

          this.pages = res.data
        } catch (error) {

          console.log('error loading pages:', error)
        }
      },
      async loadSpreadsheets () {

        try {

          const res = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, {
            headers: { 'application-unique-title': this.application }
          })

          this.spreadsheets = res.data || []
        } catch (error) {

          console.log('error loading spreadsheets:', error)
        }
      },
      async applyDesignerChange (change) {

        const keys = change && change.keys || []
        const hitsMenu = keys.includes('settings:menu') || keys.includes('settings') || keys.includes('application')

        if (!hitsMenu) return

        if (this.hasChanges || this.permissionEditorVisible) {

          raiseDesignerWarning(change, keys.includes('application') ? 'this application' : 'the menu')

          return
        }

        await this.loadSettings()
      },
      async loadSettings () {

        try {

          const res = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
            headers: { 'application-unique-title': this.application }
          })

          if (res.data && res.data.menuConfig && res.data.menuConfig.items) {

            this.menuItems = normalizeMenuItems(res.data.menuConfig.items)
            this.savedMenuItems = normalizeMenuItems(res.data.menuConfig.items)
          }

          if (res.data && res.data.logoBase64) {

            this.logoBase64 = res.data.logoBase64
            this.savedLogoBase64 = res.data.logoBase64
          }

          if (res.data && res.data.appDisplayName) {

            this.appDisplayName = res.data.appDisplayName
            this.savedAppDisplayName = res.data.appDisplayName
          }
        } catch (error) {

          console.log('error loading settings:', error)
        }
      },
      async saveMenu () {

        if (!this.hasChanges) return

        this.saving = true
        this.saved = false

        try {

          await this.$http.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
            menuConfig: { items: this.menuItems },
            logoBase64: this.logoBase64 || null,
            appDisplayName: this.appDisplayName || null
          }, {
            headers: { 'application-unique-title': this.application }
          })

          this.savedMenuItems = _.cloneDeep(this.menuItems)
          this.savedLogoBase64 = this.logoBase64
          this.savedAppDisplayName = this.appDisplayName
          this.saved = true

          setTimeout(() => { this.saved = false }, 2000)
        } catch (error) {

          console.log('error saving menu config:', error)
        } finally {

          this.saving = false
        }
      },
      generateItemId () {

        return `item_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
      },
      addPageToMenu (page) {

        this.menuItems.push({
          id: this.generateItemId(),
          type: 'link',
          label: page.title,
          pageUniqueTitle: page.uniqueTitle
        })
      },
      newDropdownItem () {

        return {
          id: this.generateItemId(),
          type: 'dropdown',
          label: 'New Group',
          children: []
        }
      },
      addDropdownGroup () {

        this.menuItems.push(this.newDropdownItem())
      },
      newValueItem () {

        return {
          id: this.generateItemId(),
          type: 'value',
          label: 'Count',
          children: [],
          valueConfig: {
            kind: 'count',
            spreadSheet: '',
            field: '',
            formula: 'COUNT({id})',
            filters: [],
            filterMatchMode: 'all',
            scopeToLoggedInUser: false
          }
        }
      },
      addValueItem () {

        this.menuItems.push(this.newValueItem())
      },
      previewText (item) {

        const entry = this.previewValues[item.id]

        if (!entry || entry.display === '' || entry.display == null) return '—'

        return entry.display
      },
      async loadPreviewValues () {

        const requestId = ++this.previewRequestId
        const hasValueItem = (items) => (items || []).some(item => item.type === 'value' || hasValueItem(item.children))
        const hasValue = hasValueItem(this.menuItems)

        if (!hasValue) {

          this.previewValues = {}

          return
        }

        try {

          const res = await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/menu-values/preview`, {
            items: this.menuItems
          }, {
            headers: { 'application-unique-title': this.application }
          })

          if (requestId !== this.previewRequestId) return

          this.previewValues = (res.data && res.data.values) || {}
        } catch (error) {

          if (requestId !== this.previewRequestId) return

          this.previewValues = {}
        }
      },
      removeMenuItem (index) {

        this.menuItems.splice(index, 1)
      },
      onCloneFromAvailable (original) {

        return {
          id: this.generateItemId(),
          type: 'link',
          label: original.title,
          pageUniqueTitle: original.uniqueTitle
        }
      },
      checkMenuMove (evt) {

        const dragged = evt.draggedContext && evt.draggedContext.element
        const depth = Number(evt.to && evt.to.dataset && evt.to.dataset.menuDepth)

        if (!depth) return true

        return canPlaceMenuItem(dragged, depth)
      },
      startEditing (itemId) {

        this.editingItemId = itemId
      },
      focusInput (e) {

        const el = e.el
        if (el && el.focus) {

          el.focus()
          el.select()
        }
      },
      triggerLogoUpload () {

        this.$refs.logoInput.click()
      },
      onLogoSelected (event) {

        const file = event.target.files[0]

        if (!file) return

        if (!file.type.startsWith('image/')) {

          alert('Please select an image file.')
          return
        }

        if (file.size > 500 * 1024) {

          alert('Logo must be smaller than 500KB.')
          return
        }

        const reader = new FileReader()

        reader.onload = (e) => {

          this.logoBase64 = e.target.result
        }

        reader.readAsDataURL(file)

        // Reset input so same file can be re-selected
        event.target.value = ''
      },
      removeLogo () {

        this.logoBase64 = null
      },
      toggleFlag (item, flag) {

        item[flag] = !item[flag]
      },
      openLinkPermissionRules (item) {

        this.permissionTarget = item
        this.permissionEditorVisible = true
      },
      saveLinkPermissionRules (rules) {

        if (!this.permissionTarget) return

        this.permissionTarget.userPermissionRules = rules
        this.permissionEditorVisible = false
      },
      toggleAlignRight (item) {

        if (item.alignRight) delete item.alignRight
        else item.alignRight = true
      },
      goBackToDesign () {

        this.$router.push(`/${this.application}/design`)
      }
    }
  }
</script>

<style scoped>
.menu-editor-root {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.menu-editor-toolbar {
  flex: 0 0 auto;
}

#context-nav {
  background-color: #181915 !important;
  border-bottom: 1px #2e2e2b solid;
}

.save-active {
  background-color: #9ad12b !important;
  border-color: #9ad12b !important;
  color: #111 !important;
}

.menu-editor-panels {
  flex: 1 1 auto;
  min-height: 0;
  flex-wrap: nowrap;
  overflow: hidden;
}

/* Left Panel */
.available-pages-col {
  padding: 0 !important;
  background-color: #141412;
  border-right: 1px solid #2e2e2b;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
}

.panel-header {
  padding: 14px 14px 10px;
  border-bottom: 1px solid #2e2e2b;
}

.panel-title {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ad12b;
  margin-bottom: 4px;
}

.panel-desc {
  font-size: 0.75rem;
  color: #a8a8a4;
}

.available-pages-list {
  padding: 8px 0;
}

.available-page-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  cursor: grab;
  color: #b0b0ae;
  font-size: 0.875rem;
  transition: background 0.1s, color 0.1s;
  user-select: none;
}

.available-page-item:hover {
  background-color: #1e1e1b;
  color: #e0e0de;
}

.available-page-item.already-in-menu {
  opacity: 0.45;
  cursor: grab;
}

.page-drag-icon {
  font-size: 0.65rem;
  color: #7a7a76;
  cursor: grab;
}

.page-icon {
  font-size: 0.75rem;
  color: #4a4a47;
  flex-shrink: 0;
}

.page-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.in-menu-badge {
  font-size: 0.65rem;
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.1);
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
}

.add-page-btn {
  font-size: 1rem;
  color: #9ad12b;
  cursor: pointer;
  padding: 0 4px;
  font-weight: bold;
  transition: color 0.15s;
}

.add-page-btn:hover {
  color: #b8f046;
}

/* Right Panel */
.menu-layout-col {
  padding: 0 !important;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
}

.unsaved-badge {
  color: #f0ad4e;
  font-size: 0.7rem;
  font-weight: 600;
}

.menu-items-container {
  padding: 8px 14px;
  min-height: 100px;
}

.menu-items-draggable {
  min-height: 72px;
}

.menu-item {
  background-color: #1a1a17;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  margin-bottom: 6px;
}

.menu-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
}

.menu-drag-handle,
.menu-child-drag-handle {
  cursor: grab;
  color: #7a7a76;
  font-size: 0.65rem;
  padding: 2px;
}

.menu-drag-handle:hover,
.menu-child-drag-handle:hover {
  color: #a8a8a4;
}

.dropdown-icon {
  color: #f0ad4e;
  font-size: 0.8rem;
}

.link-icon {
  color: #5bc0de;
  font-size: 0.7rem;
}

.item-label {
  flex: 1;
  color: #e8e8e6;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  transition: background 0.1s;
}

.item-label:hover {
  background-color: #2a2a27;
}

.label-input {
  flex: 1;
  background-color: #111;
  border: 1px solid #9ad12b;
  color: #e8e8e6;
  font-size: 0.875rem;
  padding: 2px 4px;
  border-radius: 3px;
  outline: none;
}

.item-page-ref {
  font-size: 0.7rem;
  color: #a8a8a4;
  font-family: monospace;
  white-space: nowrap;
}

.item-actions {
  display: flex;
  gap: 4px;
}

.login-toggle-btn {
  font-size: 11px;
  color: #a8a8a4;
  border: 1px #3a3a37 solid;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.login-toggle-btn:hover {
  color: #9ad12b;
  border-color: #9ad12b;
}

.login-toggle-btn.active {
  color: #9ad12b;
  border-color: #9ad12b;
  background-color: rgba(154, 209, 43, 0.1);
}

.hide-logged-in-btn {
  font-size: 11px;
  color: #a8a8a4;
  border: 1px #3a3a37 solid;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.hide-logged-in-btn:hover {
  color: #f0ad4e;
  border-color: #f0ad4e;
}

.hide-logged-in-btn.active {
  color: #f0ad4e;
  border-color: #f0ad4e;
  background-color: rgba(240, 173, 78, 0.1);
}

.remove-btn {
  font-size: 12px;
  font-weight: bolder;
  color: #f82471;
  border: 1px #f82471 solid;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s;
}

.remove-btn:hover {
  background-color: #f82471;
  color: #fff;
}

/* Dropdown children */
.dropdown-children {
  padding: 4px 14px 8px 36px;
  border-top: 1px solid #2e2e2b;
}

.dropdown-children-draggable {
  min-height: 40px;
}

.menu-child-item {
  display: flex;
  flex-direction: column;
  padding: 0;
  background-color: #161614;
  border: 1px solid #252522;
  border-radius: 3px;
  margin-bottom: 4px;
}

.menu-child-item .menu-child-row {
  padding: 6px 10px;
}

.add-child-value-btn {
  margin-top: 6px;
  background: none;
  border: 0;
  color: #9ad12b;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
}

.add-child-value-btn:hover {
  color: #b8f046;
}

.empty-dropdown-hint {
  font-size: 0.75rem;
  color: #a8a8a4;
  padding: 8px 10px;
  text-align: center;
  border: 1px dashed #2e2e2b;
  border-radius: 3px;
}

.empty-menu-hint {
  font-size: 0.85rem;
  color: #a8a8a4;
  padding: 24px;
  text-align: center;
  border: 1px dashed #2e2e2b;
  border-radius: 4px;
}

/* Drag ghost */
.drag-ghost {
  opacity: 0.4;
  background-color: #1a1a17;
  border-left: 2px solid #9ad12b;
}

/* Actions */
.menu-actions {
  display: flex;
  gap: 8px;
  padding: 8px 14px;
}

.value-icon {
  color: #9ad12b;
  font-size: 0.75rem;
}

.value-result {
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.1);
  border-radius: 10px;
  padding: 0 8px;
}

.menu-child-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.add-dropdown-btn {
  background: none;
  border: 1px dashed #2e2e2b;
  color: #9ad12b;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;
}

.add-dropdown-btn:hover {
  border-color: #9ad12b;
  color: #b8f046;
  background-color: #1e1e1b;
}

/* Preview */
.preview-section {
  padding: 14px;
  border-top: 1px solid #2e2e2b;
  margin-top: 10px;
}

.preview-navbar {
  display: flex;
  gap: 0;
  background-color: #0d6efd;
  padding: 8px 16px;
  border-radius: 4px;
  margin-top: 8px;
  min-height: 40px;
  align-items: center;
}

.preview-right-gap {
  margin-left: auto;
}

.preview-empty {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8rem;
}

.preview-link,
.preview-dropdown,
.preview-value {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  padding: 4px 12px;
  border-radius: 3px;
  transition: background 0.1s;
}

.preview-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.preview-value-link {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.preview-lock {
  font-size: 0.7rem;
  opacity: 0.8;
}

.preview-value-badge {
  border: 1px solid currentColor;
  border-radius: 999px;
  padding: 0 7px;
  font-size: 0.75rem;
  font-weight: 700;
}

.preview-link:hover,
.preview-dropdown:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.preview-logo {
  max-height: 22px;
  max-width: 80px;
  margin-right: 12px;
  object-fit: contain;
}

.remove-logo-btn {
  background-color: transparent !important;
  border-color: #f82471 !important;
  color: #f82471 !important;
  font-size: 0.8rem;
}

.remove-logo-btn:hover {
  background-color: #f82471 !important;
  color: #fff !important;
}

.app-name-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
}

.app-name-icon {
  color: #9ad12b;
  font-size: 0.8rem;
}

.app-name-input {
  background-color: #111;
  border: 1px solid #2e2e2b;
  color: #e8e8e6;
  font-size: 0.8rem;
  padding: 3px 8px;
  border-radius: 3px;
  outline: none;
  width: 150px;
  transition: border-color 0.15s;
}

.app-name-input:focus {
  border-color: #9ad12b;
}

.app-name-input::placeholder {
  color: #4a4a47;
}

.preview-app-name {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-weight: 600;
  margin-right: 16px;
  white-space: nowrap;
}
</style>
